import { z } from 'zod';
import { OUTBOUND_CLASSES, RECORDING_MODES, SECURITY_PROFILES, isValidCidrClient } from '../types/sip-extensions';
import { KamTransportSchema, KamCodecSchema, KamSrtpModeSchema, KAM_TRANSPORTS, KAM_CODECS, KAM_SIP_SETTINGS_DEFAULTS } from '../types/kam-settings';
import { getProfileDefaults, getProfileSbcDefaults } from '../helpers/security-profile';

export const sipExtensionNumberSchema = z.string().regex(/^[0-9*#]{2,10}$/, 'Interno non valido: usare da 2 a 10 cifre, * o #');
export const sipExtensionPasswordSchema = z.string().min(8).max(128).refine(
  value => value !== '[HIDDEN]' && value !== '[encrypted]' && !/^\$2[aby]\$/.test(value),
  'Inserire una nuova password SIP, non una maschera o un hash',
);
export const sipExtensionSettingsSchema = z.object({
  voicemail_enabled: z.boolean().optional(),
  dnd_enabled: z.boolean().optional(),
  recording_enabled: z.boolean().optional(),
  recording_mode: z.enum(RECORDING_MODES).optional(),
  recording_consent_required: z.boolean().optional(),
  timeout_seconds: z.number().int().min(5).max(300).optional(),
  attach_audio: z.boolean().optional(),
  delete_after_email: z.boolean().optional(),
  email_notification: z.boolean().optional(),
});
const destination = z.union([
  z.string().regex(/^(?:\+?[1-9]\d{1,14}|[0-9*#]{2,10})$/),
  z.literal(''), z.null(),
]).transform(value => value === '' ? null : value).optional();
const fields = {
  extension: sipExtensionNumberSchema,
  password: sipExtensionPasswordSchema,
  display_name: z.string().min(1).max(100),
  status: z.enum(['active', 'inactive', 'locked']).default('active'),
  type: z.enum(['user', 'queue', 'conference', 'voicemail']).default('user'),
  caller_id_number: z.string().max(50).nullable().optional(),
  sip_domain: z.string().max(255).nullable().optional(),
  room_number: z.string().max(50).nullable().optional(),
  store_id: z.string().uuid().nullable().optional(),
  limit_max: z.number().int().min(1).max(50).default(3),
  allowed_ips: z.array(z.object({
    cidr: z.string().refine(isValidCidrClient, 'CIDR non valido'),
    label: z.string().min(1).max(64),
  }).strict()).max(64).default([]),
  outbound_class: z.enum(OUTBOUND_CLASSES).default('internal_national'),
  cfwd_always_dest: destination, cfwd_noanswer_dest: destination,
  cfwd_busy_dest: destination, cfwd_unavail_dest: destination,
  moh_class: z.string().max(64).nullable().optional(),
  security_profile: z.enum(SECURITY_PROFILES).default('sip_secure'),
  security_overrides_enabled: z.boolean().default(false),
  force_tls: z.boolean().optional(),
  force_srtp: z.boolean().optional(),
  force_dtls: z.boolean().optional(),
  ai_agent_enabled: z.boolean().optional(),
  ai_agent_id: z.string().uuid().nullable().optional(),
  ai_response_mode: z.enum(['always', 'no_answer', 'busy', 'unavailable']).optional(),
  ai_no_answer_timeout: z.number().int().min(1).max(120).optional(),
  ai_fallback_extension: sipExtensionNumberSchema.nullable().optional(),
  transport_pref: z.array(KamTransportSchema).max(KAM_TRANSPORTS.length).optional(),
  codec_list: z.array(KamCodecSchema).max(KAM_CODECS.length).default(KAM_SIP_SETTINGS_DEFAULTS.codec_list),
  srtp_mode: KamSrtpModeSchema.optional(),
  dtmf_mode: z.enum(['rfc2833', 'info', 'inband']).default('rfc2833'),
  is_active: z.boolean().optional(),
  dnd_enabled: z.boolean().optional(),
  settings: sipExtensionSettingsSchema.default({}),
};
const createObject = z.object(fields);
const patchObject = createObject.partial();
export const sipExtensionPatchObjectSchema = patchObject;
export const sipExtensionConfigurationSchema = createObject.omit({ password: true });
// Inspect removed fields BEFORE stripping extra metadata from ordinary reads.
export { legacyGuard as sipExtensionLegacyInputGuard };
const legacyGuard = z.record(z.unknown()).superRefine((data, ctx) => {
  for (const key of ['voicemail_pin', 'pickup_group', 'registration_port', 'registration_expires', 'contact']) {
    if (key in data) ctx.addIssue({ code: 'custom', path: [key], message: `Campo legacy '${key}' rimosso dal modello X1` });
  }
  const settings = data.settings;
  if (settings && typeof settings === 'object') {
    for (const key of ['call_forwarding', 'recording', 'codec_prefs', 'force_tls', 'force_srtp', 'ip_whitelist', 'notes', 'timezone', 'email']) {
      if (key in settings) ctx.addIssue({ code: 'custom', path: ['settings', key], message: `Campo legacy 'settings.${key}' rimosso dal modello X1` });
    }
  }
});
function normalizeDnd<T extends { dnd_enabled?: boolean; settings?: { dnd_enabled?: boolean } }>(data: T, ctx: z.RefinementCtx): T {
  if (data.dnd_enabled !== undefined && data.settings?.dnd_enabled !== undefined
      && data.dnd_enabled !== data.settings.dnd_enabled) {
    ctx.addIssue({ code: 'custom', path: ['dnd_enabled'], message: 'Valori DND discordanti: inviare un solo stato' });
    return z.NEVER;
  }
  const normalized = { ...data };
  const enabled = data.dnd_enabled ?? data.settings?.dnd_enabled;
  if (enabled !== undefined) normalized.dnd_enabled = enabled;
  if (data.settings !== undefined) {
    normalized.settings = { ...data.settings };
    delete normalized.settings.dnd_enabled;
  }
  return normalized;
}
export const sipExtensionCreateBodySchema = legacyGuard.pipe(createObject).transform((data, ctx) => normalizeDnd(data, ctx)).transform(data => {
  const flags = getProfileDefaults(data.security_profile);
  const sbc = getProfileSbcDefaults(data.security_profile);
  return {
    ...data,
    force_tls: data.force_tls ?? flags.force_tls,
    force_srtp: data.force_srtp ?? flags.force_srtp,
    force_dtls: data.force_dtls ?? flags.force_dtls,
    transport_pref: data.transport_pref ?? [...sbc.transport_pref],
    srtp_mode: data.srtp_mode ?? sbc.srtp_mode,
  };
});
export const sipExtensionPatchBodySchema = legacyGuard.pipe(patchObject).transform((data, ctx) => normalizeDnd(data, ctx)).transform(input => {
  const output = patchObject.parse(Object.fromEntries(Object.entries(input).filter(([, value]) => value !== undefined)));
  if (output.settings) output.settings = sipExtensionSettingsSchema.parse(
    Object.fromEntries(Object.entries(output.settings).filter(([, value]) => value !== undefined)),
  );
  return output;
});
export type SipExtensionCreateRequest = z.input<typeof createObject>;
export type SipExtensionPatchRequest = z.infer<typeof sipExtensionPatchBodySchema>;
export const sipExtensionDuplicateBodySchema = z.object({ extension: sipExtensionNumberSchema }).strict();
export const sipExtensionRotateBodySchema = z.object({
  password: sipExtensionPasswordSchema.optional(), regenerate: z.boolean().optional(),
}).refine(data => data.regenerate === true || data.password !== undefined, 'Password o regenerate obbligatorio');

export const sipExtensionDndOutcomeSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('command_accepted') }).strict(),
  z.object({
    state: z.literal('not_applied'),
    reason: z.enum(['tenant_unavailable', 'node_unavailable', 'command_rejected']),
  }).strict(),
  z.object({
    state: z.literal('unconfirmed'),
    reason: z.enum(['timeout', 'unexpected_response', 'command_error']),
  }).strict(),
]);
export type SipExtensionDndOutcome = z.infer<typeof sipExtensionDndOutcomeSchema>;
const timestamp = z.union([z.string(), z.date().transform(value => value.toISOString())]);
// Explicit runtime projection: password/hash/ciphertext cannot survive this schema.
export const sipExtensionReadSchema = createObject.omit({ password: true }).partial().extend({
  id: z.string().uuid(), tenant_id: z.string().uuid(), extension: z.string(),
  display_name: z.string(), settings: sipExtensionSettingsSchema,
  external_id: z.string().uuid().nullable().optional(),
  created_at: timestamp, updated_at: timestamp,
  deleted_at: timestamp.nullable().optional(),
  is_registered: z.boolean().optional(), registered_at: timestamp.nullable().optional(),
  registration_ip: z.string().nullable().optional(), user_agent: z.string().nullable().optional(),
  last_seen: timestamp.nullable().optional(), connection_type: z.string().nullable().optional(),
  queue_logged_in: z.boolean().optional(),
  freeswitch_update: sipExtensionDndOutcomeSchema.optional(),
});
export type SipExtensionReadDto = z.infer<typeof sipExtensionReadSchema>;
export const sipExtensionListSchema = z.object({
  items: z.array(sipExtensionReadSchema),
  pagination: z.object({ page: z.number(), limit: z.number(), total: z.number(), totalPages: z.number() }),
});
export type SipExtensionListDto = z.infer<typeof sipExtensionListSchema>;
export const sipExtensionCredentialSchema = z.object({
  extension: z.string(), sip_username: z.string(), sip_password: z.string(),
  display_name: z.string().optional(), sip_domain: z.string().optional(),
});
export type SipExtensionCredentialDto = z.infer<typeof sipExtensionCredentialSchema>;
export const sipExtensionProvisioningSchema = sipExtensionCredentialSchema.extend({
  password: z.string().min(1), display_name: z.string(), sip_domain: z.string().min(1),
  wss_url: z.string().min(1), wss_enabled: z.boolean(), sip_server: z.string().min(1),
  sip_port_udp: z.number(), sip_port_tls: z.number(), sip_port_wss: z.number(),
  outbound_proxy: z.string(), transport: z.literal('tls'),
});
export type SipExtensionProvisioningDto = z.infer<typeof sipExtensionProvisioningSchema>;
export const sipExtensionOwnProvisioningSchema = sipExtensionProvisioningSchema.extend({
  transport: z.literal('wss'),
  status: z.enum(['active', 'inactive', 'locked']),
  type: z.enum(['user', 'queue', 'conference', 'voicemail']),
  is_registered: z.boolean(),
  connection_type: z.enum(['wss', 'udp', 'sip']).nullable(),
});
export type SipExtensionOwnProvisioningDto = z.infer<typeof sipExtensionOwnProvisioningSchema>;
export const sipExtensionOwnStatusSchema = sipExtensionOwnProvisioningSchema.pick({
  extension: true, status: true, type: true, is_registered: true, connection_type: true,
});
export type SipExtensionOwnStatusDto = z.infer<typeof sipExtensionOwnStatusSchema>;
/** SIP payload carried by tenant login and native-mobile provisioning. */
export const sipClientConfigSchema = z.object({
  server: z.string().min(1),
  port: z.number().int().min(1).max(65535),
  username: z.string().min(1),
  password: z.string().min(1),
  realm: z.string().min(1),
  transport: z.literal('TLS'),
  codecs: z.array(z.string()),
  callerIdName: z.string(),
  callerIdNumber: z.string(),
  stunServer: z.string(),
});
export type SipClientConfigDto = z.infer<typeof sipClientConfigSchema>;
export const sipMobileConfigSchema = sipClientConfigSchema.extend({
  tenantSlug: z.string().min(1),
  tenantName: z.string(),
});
export type SipMobileConfigDto = z.infer<typeof sipMobileConfigSchema>;
export const sipProvisioningErrorCodeSchema = z.enum([
  'SIP_ENCRYPTION_NOT_CONFIGURED',
  'SIP_CREDENTIAL_HASH_ONLY',
  'SIP_CREDENTIAL_UNAVAILABLE',
  'SIP_PROVISIONING_FAILED',
]);
export const sipProvisioningErrorSchema = z.object({
  code: sipProvisioningErrorCodeSchema,
  message: z.string(),
});
export type SipProvisioningErrorDto = z.infer<typeof sipProvisioningErrorSchema>;
export const sipLoginProvisioningSchema = z.object({
  sipConfig: sipClientConfigSchema.nullable(),
  sipConfigError: sipProvisioningErrorSchema.optional(),
});
export type SipLoginProvisioningDto = z.infer<typeof sipLoginProvisioningSchema>;
export const sipDiagnosticSchema = z.object({
  state: z.enum(['registered', 'not_registered', 'unknown', 'error']),
  observed_at: z.string(), message: z.string(),
  logs: z.array(z.object({
    timestamp: z.string(), level: z.enum(['info', 'success', 'warning', 'error']), message: z.string(),
  })),
});
export type SipDiagnosticDto = z.infer<typeof sipDiagnosticSchema>;
export const sipExtensionRotationSchema = sipExtensionCredentialSchema.extend({ password: z.string().optional() });
export type SipExtensionRotationDto = z.infer<typeof sipExtensionRotationSchema>;

export const sipExtensionDndBodySchema = z.object({ enabled: z.boolean() });
export type SipExtensionDndBody = z.infer<typeof sipExtensionDndBodySchema>;
export const sipExtensionDndStateSchema = z.object({
  id: z.string().uuid(),
  extension: z.string(),
  dnd_enabled: z.boolean(),
});
export type SipExtensionDndState = z.infer<typeof sipExtensionDndStateSchema>;
export const sipExtensionDndResponseSchema = sipExtensionDndStateSchema.extend({
  freeswitch_update: sipExtensionDndOutcomeSchema,
});
export type SipExtensionDndResponse = z.infer<typeof sipExtensionDndResponseSchema>;

export const sipExtensionQueueOutcomeSchema = z.union([
  sipExtensionDndOutcomeSchema,
  z.object({
    state: z.literal('not_applied'),
    reason: z.enum(['queues_unavailable', 'no_active_queues']),
  }).strict(),
  z.object({ state: z.literal('unconfirmed'), reason: z.literal('partial_application') }).strict(),
]);
export type SipExtensionQueueOutcome = z.infer<typeof sipExtensionQueueOutcomeSchema>;
export const sipExtensionQueueStateSchema = z.object({
  extension: z.string(),
  queue_logged_in: z.boolean(),
});
export type SipExtensionQueueState = z.infer<typeof sipExtensionQueueStateSchema>;
export const sipExtensionQueueResponseSchema = sipExtensionQueueStateSchema.extend({
  freeswitch_update: sipExtensionQueueOutcomeSchema,
});
export type SipExtensionQueueResponse = z.infer<typeof sipExtensionQueueResponseSchema>;
